import { Box, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { useUserProjects, useUsersList } from '../../query/apiQueries';
import { usersTableHead } from '../../static-data/users';
import UserActions from './UserActions';
import UsersTableAction from './UsersTableAction';
import CustomSelect from '../../ui/primitives/CustomSelect';
import TableComponent from '../../ui/data/table/TableComponent';
import SearchBar from '../../ui/primitives/SearchBar';
import { useUser } from '../../context/UserContext';
import { useAccess } from '../../hooks/useAccess';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const UsersList = () => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [searchValue, setSearchValue] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [perPage, setPerPage] = useState(10);

  const { currentUser } = useUser();
  const { canAccess } = useAccess();
  const canManageUsers = canAccess('ManageUsers');

  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const statusFilter = showInactive ? 'inactive' : 'exclude-inactive';

  const {
    data: usersData,
    isLoading: isUsersLoading,
    refetch: refetchUsers,
  } = useUsersList({
    perPage,
    page,
    search: debouncedSearchQuery,
    status: statusFilter,
    includeProject: true,
    projectId: selectedProject,
  });

  const { data: projectData, isLoading: isProjectsLoading } = useUserProjects({
    perPage: 'all',
    page: 1,
    search: null,
    includeTestCases: false,
    includeLinkedProjects: true,
  });
  const users = usersData?.users || [];
  const totalCount = usersData?.count || 0;
  const projects = projectData?.projects || [];

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };
  const handleShowInactiveChange = (value) => {
    setShowInactive(value);
    setSelected([]);
    setPage(1);
  };

  const handleProjectChange = (value) => {
    setSelectedProject(value === 'all' ? null : value);
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleRefetchUsers = () => {
    refetchUsers();
    setSelected([]);
  };

  const projectOptions = [
    { id: 'all', name: 'All Projects' },
    ...projects.map((project) => ({
      id: project.id,
      name: project.name.length > 20
        ? `${project.name.slice(0, 20)}...`
        : project.name,
    })),
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonOutlineIcon color="primary" /> Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View team members, invite new users, and manage their project assignments.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm>
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search Users"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <CustomSelect
            id="projectSelect"
            value={selectedProject ?? 'all'}
            onChange={handleProjectChange}
            options={projectOptions}
            disabled={isProjectsLoading}
          />
        </Grid>

        <Grid item xs={12} sm="auto" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <UsersTableAction
            fetchUsersData={handleRefetchUsers}
            showInactive={showInactive}
            setShowInactive={handleShowInactiveChange}
            seletectItems={selected}
            setSelected={setSelected}
            currentUser={currentUser}
          />
        </Grid>
      </Grid>

      <TableComponent
        data={users.map((user) => ({
          ...user,
          isRowDisabled: !canManageUsers,
        }))}
        headerTabs={usersTableHead}
        page={page}
        setPage={setPage}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        defaultSort="created_at"
        isLoading={isUsersLoading}
        showCheckbox={canManageUsers}
        striped
        totalCount={totalCount}
        showPages
        handlePageChange={setPage}
        tablekey="users"
        setSelected={setSelected}
        selected={selected}
        showCount
        showRowCount={true}
        onRefresh={handleRefetchUsers}
        setPerPage={handlePageSizeChange}
        action={(row) =>
          canManageUsers ? (
            <UserActions
              row={row}
              fetchCustomerList={refetchUsers}
              currentUser={currentUser}
            />
          ) : null
        }
      />
    </Box>
  );
};

export default UsersList;